<template>
  <v-form
    ref="form"
    v-model="valid"
    class="main-expandable-panel panel-row d-flex incomplete"
    autocomplete="off"
    @submit.prevent=""
  >
    <div class="panel-cell drag-cell flex-grow-0 flex-shrink-0" />

    <div class="panel-cell name-description pr-0">
      <div class="top-container d-flex">
        <AssortmentTextField
          v-model="model.name"
          :field-key="nameKey"
          data-id-e2e="bundleName"
          :data-value-e2e="model.name"
          :disabled="isEditingDisabled"
          :rules="[required, v => isNameUnique(v, bundle.name, bundleNames, $t('entities.bundle'))]"
          @blur="saveModel"
          @keyup.enter="saveModel"
          @keyup.esc="resetModel('name')"
        />
      </div>
      <AssortmentTextField
        v-model="model.description"
        :field-key="descriptionKey"
        data-id-e2e="bundleDescription"
        :data-value-e2e="model.description"
        :disabled="isEditingDisabled"
        @blur="saveModel"
        @keyup.enter="saveModel"
        @keyup.esc="resetModel('description')"
      />
    </div>

    <section class="panel-cell align-start flex-column flex-grow-1" />

    <section class="align-start go-live flex-column">
      <TargetLaunch
        v-model="model.targetLaunchDate"
        data-id-e2e="bundleGoLive"
        :disabled="isEditingDisabled"
        :title="$t('bundlesPage.goLive')"
        @update:model-value="saveModel"
      />
    </section>

    <section class="panel-cell d-flex align-center">
      <div class="d-flex align-center justify-space-between">
        <div class="icons">
          <Notes :title="$t('notes.types.workpackage')" :disabled="!showNotImplemented" />
        </div>
        <BundleOptionUnarchive
          v-if="model.archived"
          :workpackage="model"
          :entity="entities.bundle"
        />
        <BundleOptionArchive v-else :workpackage="model" :entity="entities.bundle" />
      </div>
    </section>

    <section class="panel-cell align-start flex-column">
      <h4 class="header">
        {{ tkey('bundlesPage.creator') }}
      </h4>

      <div class="d-flex align-center flex-end row justify-end">
        <AssortmentAvatar :username="getName(bundle)" />
      </div>
    </section>

    <div class="panel-cell px-1 expand align-center justify-center flex-grow-0 flex-shrink-0">
      <MainExpandButton
        :disabled="isExpandDisabled"
        :is-expanded="isExpanded"
        @expand="handleExpanded"
      />
    </div>
  </v-form>
</template>

<script>
import { map, get, assign } from 'lodash';
import entities from '@enums/entities';
import { useBundlesStore } from '@/stores/bundles';
import { useFeature } from '@/composables/use-feature';
import { usePermission } from '@/composables/use-permission';
import { useInputValidations } from '@/composables/input-validations';
import { useInternationalization } from '@/composables/use-internationalization';
import { useDayjs } from '@/composables/use-dayjs';

export default {
  props: {
    bundle: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const bundlesStore = useBundlesStore();

    const { canEditBundles } = usePermission();
    const { showNotImplemented } = useFeature();
    const { formatDateForSave } = useDayjs();

    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();
    const { tkey } = useInternationalization();

    return {
      bundlesStore,
      formatDateForSave,
      canEditBundles,
      showNotImplemented,
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      tkey,
    };
  },

  data() {
    return {
      model: {
        _id: this.bundle._id,
        name: this.bundle.name,
        description: this.bundle.description,
        targetLaunchDate: this.bundle.targetLaunchDate,
        archived: this.bundle.archived,
      },
      valid: true,
      nameKey: false,
      descriptionKey: false,
      entities,
    };
  },

  computed: {
    bundleNames() {
      return map(this.bundlesStore.bundles, 'name');
    },

    isExpanded() {
      if (!this.bundlesStore.selectedBundle) return false;
      return (
        this.bundlesStore.isWorkpackagePanelExpanded &&
        this.bundlesStore.selectedBundle._id === this.bundle._id
      );
    },

    isExpandDisabled() {
      return !this.bundle._id;
    },

    isEditingDisabled() {
      return !this.canEditBundles;
    },
  },

  methods: {
    resetModel(field) {
      this[`${field}Key`] = !this[`${field}Key`]; // toggle the key value so the component is reset
      this.updateModel(field, this.bundlesStore.getBundleById(this.bundle._id)[field]);
    },

    updateModel(field, value) {
      this.model[field] = value;
    },

    async saveModel() {
      if (!this.valid) return false;
      const targetLaunchDate = this.formatDateForSave(
        this.model.targetLaunchDate,
        this.getDefaultReverseFormat
      );

      await this.bundlesStore.updateBundle({
        bundleId: this.bundle._id,
        updates: assign(this.bundle, this.model, { targetLaunchDate }),
      });

      this.nameKey = !this.nameKey;
    },

    handleExpanded() {
      this.bundlesStore.toggleWorkpackagePanelExpandedMutation({ bundle: this.bundle });
    },

    getName(bundle) {
      return get(bundle, 'createdBy.fullName');
    },

    validate() {
      return this.$refs.form.validate();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.panel-row {
  background: $assortment-bundle-bg-colour;
}

.panel-cell {
  border-right: 1px solid $assortment-bundle-panel-divider-colour;
  flex-basis: max-content;
  margin-top: 7px;

  &:first-child {
    border-right: none;
  }
}

.disabled {
  color: $assortment-disabled-text-button-colour;
  pointer-events: none;

  :deep(.tooltip) {
    color: $assortment-disabled-text-button-colour;
  }
}

.drag-cell {
  background: $assortment-bundle-drag-cell-colour;
  flex-basis: auto;
}

.name-description {
  :deep() {
    .v-input__control {
      background-color: $assortment-bundle-input-bg-colour;
      border: 1px dashed $assortment-bundle-edit-border-colour;
    }
  }
}
</style>
